/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
// eslint-disable-next-line import-x/default
import containerSizing, {
  type ContainerSizing,
} from 'frontend-utils/decorators/container-sizing';
import type { Hotel, Hotelinfo, Ota } from 'frontend-utils/models';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { Day, Period } from 'lumen/models';
import type { PeriodRate } from 'lumen/models/period';
import type HotelRatesRoute from 'lumen/routes/hotel/rates';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  HEADER_HEIGHT,
  RATE_PARAMS,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import { OTB_SS_ENTRY_POINTS } from 'lumen/utils/constants/otb-selfservice-constants';
import type { TableComparisonLayout } from 'lumen/utils/constants/rate-constants';
import {
  DETAIL_TABS,
  RATE_COLUMNS,
  RATES_PAGE_MODES,
  RATES_VIEWS,
  TABLE_COMPARISON_LAYOUT,
  TABLE_COMPARISON_LAYOUT_DEFAULT,
} from 'lumen/utils/constants/rate-constants';
import { getUpdateTimestampFromExtractDatetimes } from 'lumen/utils/get-update-timestamp-from-extract-datetimes';
import {
  isComparingRates,
  isDateCompare,
} from 'lumen/utils/is-comparing-rates';
import otbOutOfSync from 'lumen/utils/otb-out-of-sync';
import {
  formatUrl,
  mapCompareParamsToAPIParams,
} from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';

interface TableSettingOption {
  groupName?: string;
  id: string;
  label?: string;
  options?: TableSettingOption[];
  unsafe_subLabel?: string;
}

// @ts-expect-error: Interface incorrectly extends interface 'Controller<unknown>'.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface HotelRatesController extends ContainerSizing, Controller {}

// @ts-expect-error: containerSizing decorator signature mismatch
@containerSizing
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class HotelRatesController extends Controller {
  declare model: ModelFrom<HotelRatesRoute>;

  // @ts-expect-error: Property 'queryParams' in type 'HotelRatesController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    { compareType: { scope: 'controller' } },
    { compareValue: { scope: 'controller' } },
    { compsetId: { scope: 'controller' } },
    { highlight: { scope: 'controller' } },
    { los: { scope: 'controller' } },
    { maxPersons: { scope: 'controller' } },
    { mealPlan: { scope: 'controller' } },
    { membershipType: { scope: 'controller' } },
    { month: { scope: 'controller' } },
    { otaId: { scope: 'controller' } },
    { platform: { scope: 'controller' } },
    { rateParam: { scope: 'controller' } },
    { roomType: { scope: 'controller' } },
    { scheduleReport: { scope: 'controller' } }, // Allows us to filter for screen recordings with schedule modal in Inspectlet
    { view: { scope: 'controller' } },
  ] as const;

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare localStorage: Services['local-storage'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare navigation: Services['navigation'];
  @service declare rates: Services['rates'];
  @service declare router: Services['router'];
  @service declare shortTermRentalUpsell: Services['short-term-rental-upsell'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Height of OTB/STR upsell banner (if present) shown in all views. Required to take into account while calculating content heights
  @tracked bannerHeight = 0;
  @tracked compareType: string = DEFAULT_QP_VALUES.hotel.rates.compareType;
  @tracked compareValue: string = DEFAULT_QP_VALUES.hotel.rates.compareValue;
  @tracked compsetId: string = DEFAULT_QP_VALUES.hotel.rates.compsetId;
  @tracked hiddenColumnIds: string[] | undefined;
  @tracked isShowingCalendarSettingsModal = false;
  @tracked isShowingPAUpsellModal = false;
  @tracked los = DEFAULT_QP_VALUES.hotel.rates.los;
  @tracked maxPersons = DEFAULT_QP_VALUES.hotel.rates.maxPersons;
  @tracked mealPlan = DEFAULT_QP_VALUES.hotel.rates.mealPlan;
  @tracked membershipType = DEFAULT_QP_VALUES.hotel.rates.membershipType;
  @tracked month: string | null = null;
  @tracked otaId: string = DEFAULT_QP_VALUES.hotel.rates.otaId;
  @tracked platform = DEFAULT_QP_VALUES.hotel.rates.platform;
  @tracked rateParam: string = DEFAULT_QP_VALUES.hotel.rates.rateParam;
  @tracked roomType = DEFAULT_QP_VALUES.hotel.rates.roomType;
  // is used as URL parameter
  @tracked scheduleReport = false;
  @tracked tableComparisonLayoutLocalState: TableComparisonLayout | undefined;
  // view selections
  @tracked view: string = DEFAULT_QP_VALUES.hotel.rates.view;
  /*
    Tracked object to store the state of the PA upsell banner when the user dismisses it
    Why? When user dismisses a banner, we store this information in localStorage directly.
    But making changes to the localStorage doesn't trigger reactivity and as a result UI doesn't update accordingly i.e. the banner doesn't disappear.
    Therefore, we also update this information in a tracked object in order to trigger reactivity.
  */
  bannerVisibilityStates = trackedObject<Record<string, boolean>>();
  highlight: string | null = null;

  // Makes sure the main loading bar is positioned underneath all fixed headers on the page
  mainLoadingBarPositionTop = HEADER_HEIGHT + SUB_HEADER_HEIGHT;
  pageMode = RATES_PAGE_MODES.RATES;

  @computed('liveupdate.activeLiveupdatesForThisSelection.length')
  get activeLiveUpdate(): number | undefined {
    return this.liveupdate.activeLiveupdatesForThisSelection?.length;
  }

  // Reserve space for the 2 main headers + 3rd new filter bar header + main content padding bottom + main content padding top + calender header
  get calendarReservedHeight(): number {
    let effectiveHeight = HEADER_HEIGHT + SUB_HEADER_HEIGHT + 16 + 8 + 30;

    if (this.bannerHeight) {
      // Reserve extra space for banner height + 16px margin bottom on banner wrapper element
      effectiveHeight += this.bannerHeight + 16;
    }

    return effectiveHeight;
  }

  @dependentKeyCompat
  get canLiveShopForOTA(): boolean {
    return this.hotel.canLiveShopForOTA(
      this.otaId,
      this.access.hasFeature('rates.liveshop.ctrip'),
    );
  }

  get compareDisabledTooltipText(): string {
    // OTA is not supported
    if (this.selectedOTA && !this.selectedOTA.hasRatechange) {
      return this.intl.t('general.comparison_disabled_for_ota');
    }

    return this.intl.t('general.comparison_disabled_for_past');
  }

  @dependentKeyCompat
  get competitorsForCurrentHotelAndOTA(): Hotelinfo[] | null {
    if (this.hotelAndMarketService.activeCompset?.competitors) {
      return this.hotelAndMarketService.activeCompset.competitors.filter(
        ({ otas }) => {
          return otas.some(({ id }) => id === this.otaId);
        },
      );
    }

    return null;
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.monthMoment.startOf('month'),
      end: this.monthMoment.endOf('month'),
    };
  }

  get dayLiveupdateEnabled(): boolean {
    return this.access.hasRatesDayLiveupdate && this.canLiveShopForOTA;
  }

  get emptyStateOtbBannerEntryPoint(): string | null {
    if (this.isCalendarView) {
      return OTB_SS_ENTRY_POINTS.RATES_CALENDAR;
    }
    if (this.isTableView) {
      return OTB_SS_ENTRY_POINTS.RATES_TABLE;
    }
    if (this.isChartView) {
      return OTB_SS_ENTRY_POINTS.RATES_CHART;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportChoices() {
    const disableEverythingExceptCustomAndSchedule =
      this.noDataForSelection?.exportDisabled;

    if (this.hasExtendedExport) {
      return [
        {
          groupName: this.intl.t('general.export_group.export'),
          options: [
            {
              id: 'defined_report',
              selection: this.hotel.getRatesExportShopDays(
                this.otaId,
                this.los,
              ),
              label: this.intl.t('general.next_days', {
                count: this.hotel.getRatesExportShopDays(this.otaId, this.los),
              }),
              disabled: disableEverythingExceptCustomAndSchedule,
            },
            {
              id: 'defined_report',
              selection: 365,
              label: this.intl.t('general.next_days', { count: 365 }),
              disabled: disableEverythingExceptCustomAndSchedule,
            },
            {
              id: 'defined_report',
              label: this.intl.t('rates.active_month'),
              disabled: disableEverythingExceptCustomAndSchedule,
            },
            {
              id: 'custom_report',
              label: this.intl.t('rates.custom'),
              disabled: this.platform > PLATFORMS.PLATFORM_DESKTOP,
              message: 'general.not_available_for_selection',
            },
          ],
        },
        {
          groupName: this.intl.t('reports.schedule_report'),
          options: [
            {
              id: 'schedule_report',
              label: this.intl.t('general.export_options.reports'),
              disabled: this.isScheduleDisabled.disabled,
              icon: 'calendar-clock',
            },
          ],
        },
      ];
    }

    return [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'defined_report',
            selection: this.hotel.getRatesExportShopDays(this.otaId, this.los),
            label: this.intl.t('general.next_days', {
              count: this.hotel.getRatesExportShopDays(this.otaId, this.los),
            }),
            disabled: disableEverythingExceptCustomAndSchedule,
          },
          {
            id: 'defined_report',
            label: this.intl.t('rates.active_month'),
            disabled: disableEverythingExceptCustomAndSchedule,
          },
          {
            id: 'custom_report',
            label: this.intl.t('rates.custom'),
            disabled: this.platform > PLATFORMS.PLATFORM_DESKTOP,
            message: 'general.not_available_for_selection',
          },
        ],
      },
      {
        groupName: this.intl.t('reports.schedule_report'),
        options: [
          {
            id: 'schedule_report',
            label: this.intl.t('reports.schedule_report'),
            disabled: this.isScheduleDisabled.disabled,
            icon: 'calendar-clock',
          },
        ],
      },
    ];
  }

  get flatTableSettingOptions(): TableSettingOption[] {
    return this.tableSettingOptions
      .flatMap((option) => option.options ?? [option])
      .filter(({ id }) => {
        // Exclude table comparison layout option from column options
        return !(Object.values(TABLE_COMPARISON_LAYOUT) as string[]).includes(
          id,
        );
      });
  }

  /**
   * Decides whether you can see the exports for longer times (full year for exmaple)
   */
  get hasExtendedExport(): boolean {
    return (
      (this.access.hasRatesCustomLiveupdate && this.canLiveShopForOTA) ||
      this.otaId === 'allchannels'
    );
  }

  get hasPricingAndDistribution(): boolean {
    return (
      this.access.hasFeature('app.channel_manager') &&
      this.access.hasFeature('app.pricing_assistant')
    );
  }

  get hiddenColumnIdsWithFallback(): string[] {
    if (this.hiddenColumnIds) {
      return this.hiddenColumnIds;
    }

    const localStorageEntry = this.localStorage.getItem(
      this.localStorageKeyForHiddenColumns,
    );
    if (localStorageEntry) {
      // @ts-expect-error: Incorrect type
      return JSON.parse(localStorageEntry) as unknown;
    }

    // Make all columns visible
    return [];
  }

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get isBestFlex(): boolean {
    return this.rateParam === RATE_PARAMS.BEST_FLEX;
  }

  get isCalendarView(): boolean {
    return this.view === RATES_VIEWS.CALENDAR;
  }

  get isChartView(): boolean {
    return this.view === RATES_VIEWS.CHART;
  }

  get isCompareDisabled(): boolean {
    // Disable compare for past months and OTAs with no hasRateChange flag
    return (
      this.isMonthInPast ||
      Boolean(this.selectedOTA && !this.selectedOTA.hasRatechange)
    );
  }

  get isCurrentOrFutureDate(): boolean {
    return this.monthMoment.isSameOrAfter(
      this.hotelAndMarketService.getLocalTime(),
      'month',
    );
  }

  get isLoading(): boolean {
    return this.isPeriodsRequestLoading || this.isRoomtypeRequestLoading;
  }

  get isMonthInPast(): boolean {
    // Compare is for future dates only
    return this.monthMoment.isBefore(
      this.hotelAndMarketService.getLocalTime(),
      'month',
    );
  }

  @dependentKeyCompat
  get isPast(): boolean {
    return this.hotelAndMarketService
      .toLocalTime(this.month)
      .isBefore(
        this.hotelAndMarketService.getLocalTime().startOf('month'),
        'month',
      );
  }

  get isPAUpsellBannerNotDismissed(): boolean {
    // If bannerVisibilityStates has the key, return value from there
    if (
      this.bannerVisibilityStates[this.localStorageKeyPAUpsellBannerVisibility]
    ) {
      return Boolean(
        this.bannerVisibilityStates[
          this.localStorageKeyPAUpsellBannerVisibility
        ],
      );
    }

    // Else, fetch PA upsell banner state from local storage
    const stored = this.localStorage.getItem(
      this.localStorageKeyPAUpsellBannerVisibility,
    );
    if (stored) {
      // @ts-expect-error: Incorrect type
      return JSON.parse(stored) as unknown;
    }

    return true;
  }

  get isPAUpsellBannerVisible(): boolean {
    return (
      this.access.hasFeature('pricing_assistant.upsell') &&
      this.isPAUpsellBannerNotDismissed
    );
  }

  @computed(
    'model.{periods.isLoading,pricingAssistantRatesRequestTaskResult.isRunning}',
  )
  get isPeriodsRequestLoading(): boolean {
    return Boolean(this.model.periods?.isLoading);
  }

  get isPolling(): boolean {
    return this.liveupdate.isPolling;
  }

  get isRatesUpsellBannerVisible(): boolean {
    return (
      this.shortTermRentalUpsell.canShowShortTermRentalUpsellBanner ||
      this.myotbSs.isOtbSelfServiceUpsellBannerVisible ||
      this.isPAUpsellBannerVisible
    );
  }

  @computed('model.roomtypes.isLoading')
  get isRoomtypeRequestLoading(): boolean {
    return Boolean(this.model.roomtypes?.isLoading);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isScheduleDisabled() {
    // Disable scheduling reports when comparing rates (except when comparing date changes)
    if (Boolean(this.compareType) && this.compareType !== 'extract_date') {
      return {
        disabled: true,
        message: this.intl.t('reports.no_report_for_comparison'),
      };
    }

    if (this.rateParam === RATE_PARAMS.THIRD_PARTY) {
      return {
        disabled: true,
        message: this.intl.t('reports.no_report_for_third_party'),
      };
    }

    // Check if los is higher than highest los available in shop config
    const message = this.hotelAndMarketService.getReportSchedulingMessage(
      'rates',
      this.los,
      this.selectedOTA,
      this.membershipType,
      this.maxPersons,
      this.platform,
    );

    return {
      disabled: Boolean(message),
      message,
    };
  }

  get isTableView(): boolean {
    return this.view === RATES_VIEWS.TABLE;
  }

  @dependentKeyCompat
  get isValidMonthToShowUpdateTime(): boolean {
    return !this.monthMoment
      .endOf('month')
      .isBefore(this.hotelAndMarketService.getLocalTime());
  }

  get liveupdateEnabled(): boolean {
    return this.access.hasRatesCustomLiveupdate;
  }

  get localStorageKeyForHiddenColumns(): string {
    return `${this.hotelAndMarketService.hotel.id}-${LOCAL_STORAGE_KEYS.RATE_TABLE_COLUMNS}`;
  }

  get localStorageKeyPAUpsellBannerVisibility(): string {
    return `isPricingAssistantUpsellBannerVisible_${this.hotelAndMarketService.hotel?.id}`;
  }

  @dependentKeyCompat
  get monthMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(`${this.month}-15`);
  }

  @computed('monthMoment', 'periods.[]', 'isPeriodsRequestLoading')
  get monthOnlyModel(): Period[] {
    if (this.isPeriodsRequestLoading) {
      return [];
    }

    const startUnix = Math.floor(
      (this.monthMoment.startOf('month').unix() +
        this.monthMoment.utcOffset() * 60) /
        86400,
    );
    const endUnix = Math.floor(
      (this.monthMoment.endOf('month').startOf('day').unix() +
        this.monthMoment.utcOffset() * 60) /
        86400,
    );

    return this.periods.filter((period): period is Period => {
      const { day } = period;
      return (
        day?.integerDate !== undefined &&
        day.integerDate >= startUnix &&
        day.integerDate <= endUnix
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthOnlyOccupancies() {
    return this.monthOnlyModel.map((period) => period.day.occupancy);
  }

  @computed(
    'access.hasRatesCustomLiveupdate',
    'activeLiveUpdate',
    'canLiveShopForOTA',
    'compareType',
    'compareValue',
    'competitorsForCurrentHotelAndOTA.[]',
    'hotel.hotelinfo.id',
    'isPast',
    'isPeriodsRequestLoading',
    'monthMoment',
    'monthOnlyModel.length',
    'otaId',
    'periods',
    'selectedOTA.hasLiveShop',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noDataForSelection() {
    if (!this.isPeriodsRequestLoading && this.periods?.[0]?.rates) {
      let hotelAndCompset: Hotelinfo[] = [this.hotel.hotelinfo];
      const compset = this.competitorsForCurrentHotelAndOTA;
      hotelAndCompset = hotelAndCompset.concat(compset ?? []);
      const total = hotelAndCompset.length * this.monthOnlyModel?.length;

      let ownHotelHasRates = false;

      if (this.monthOnlyModel.length) {
        ownHotelHasRates =
          this.monthOnlyModel
            .map((period): number => {
              const rateObj = period.rates[this.hotel.hotelinfo.id!];
              // We had issues where the rateObj was not present for a specific hotelinfo
              return rateObj === undefined ||
                rateObj[0]?.message === 'general.missing'
                ? 0
                : 1;
            })
            .reduce((a, b) => a + b) > 0;
      }

      const percentageMissingRates = this.calculateMissingRatio(
        hotelAndCompset,
        total,
        false,
      );

      const { compareType, compareValue } = this;
      let liveUpdateDisabled = false;

      if (ownHotelHasRates && !isComparingRates(compareType, compareValue)) {
        return {
          exportDisabled: false,
          hasMessage: false,
          liveUpdateDisabled,
          message: null,
          showOverlay: false,
        };
      }

      let performLiveShopMessage = this.intl.t('rates.nodata.refresh');

      if (isComparingRates(compareType, compareValue)) {
        const percentageMissingRateChanges = this.calculateMissingRatio(
          hotelAndCompset,
          total,
          true,
        );

        // Show an overlay message if too many rate changes are missing
        if (percentageMissingRates + percentageMissingRateChanges > 0.99) {
          if (!this.selectedOTA?.hasLiveShop) {
            performLiveShopMessage = this.intl.t('rates.nodata.noliveshop');
          } else if (isDateCompare(this.compareType)) {
            performLiveShopMessage = this.intl.t('rates.compare.not_allowed');
            liveUpdateDisabled = true;
          }

          return {
            exportDisabled: true,
            hasMessage: true,
            liveUpdateDisabled,
            message: performLiveShopMessage,
            // Don't show overlay when the hotel does not have custom (=month) liveupdates or the user will be stuck
            showOverlay:
              !this.activeLiveUpdate && this.access.hasRatesCustomLiveupdate,
          };
        }
      }

      if (this.isPast) {
        if (percentageMissingRates > 0.9) {
          return {
            exportDisabled: true,
            hasMessage: true,
            liveUpdateDisabled,
            message: this.intl.t('rates.nodata.noliveshop'),
            // Show in past since you can't liveshop anyway
            showOverlay: true,
          };
        }
      } else if (this.monthMoment.isSame(dayjs.utc(), 'month')) {
        if (!this.canLiveShopForOTA) {
          if (percentageMissingRates > 0.9) {
            return {
              exportDisabled: true,
              hasMessage: true,
              liveUpdateDisabled,
              message: this.intl.t('rates.nodata.noliveshop'),
              // Show it if it does not have liveupdates (can't do day liveupdates then)
              showOverlay: true,
            };
          }
        } else if (percentageMissingRates === 1) {
          return {
            exportDisabled: true,
            hasMessage: !this.activeLiveUpdate,
            message: performLiveShopMessage,
            liveUpdateDisabled,
            // Show it if it has custom liveupdates or if no liveshop is active
            showOverlay:
              !this.activeLiveUpdate && this.access.hasRatesCustomLiveupdate,
          };
        }
      } else if (!this.canLiveShopForOTA) {
        if (percentageMissingRates > 0.5) {
          return {
            exportDisabled: true,
            hasMessage: true,
            liveUpdateDisabled,
            message: this.intl.t('rates.nodata.noliveshop'),
            // Show it if it does not have liveupdates (can't do day liveupdates then)
            showOverlay: true,
          };
        }
      } else if (percentageMissingRates > 0.51) {
        // 51% because there was a case where a hotel had only one competitor an he wasn't matched for that OTA, but he still wanted to see the rates for his competitor. 51% means the overlay will never show unless both are missing
        return {
          exportDisabled: true,
          hasMessage: !this.activeLiveUpdate,
          liveUpdateDisabled,
          message: performLiveShopMessage,
          // Show it if it has custom liveupdates or if no liveshop is active
          showOverlay:
            !this.activeLiveUpdate && this.access.hasRatesCustomLiveupdate,
        };
      }
    }

    return {
      exportDisabled: false,
      hasMessage: false,
      liveUpdateDisabled: false,
      message: null,
      showOverlay: false,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbOutOfSyncUtil() {
    const occupancies = this.monthOnlyOccupancies.filter(
      (occupancy) => occupancy !== undefined,
    );

    return otbOutOfSync(
      occupancies,
      this.hotelAndMarketService,
      this.intl,
      this.access,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get params() {
    return {
      compareType: this.compareType,
      compareValue: this.compareValue,
      compsetId: this.compsetId,
      los: this.los,
      maxPersons: this.maxPersons,
      mealPlan: this.mealPlan,
      membershipType: this.membershipType,
      otaId: this.otaId,
      platform: this.platform,
      rateParam: this.rateParam,
      roomType: this.roomType,
      view: this.view,
    };
  }

  @computed('model.periods.data')
  get periods(): Partial<Period>[] {
    return this.model.periods?.data;
  }

  get ratesEvolutionOrBookingCurveEnabled(): boolean {
    return (
      Boolean(this.selectedOTA?.hasRatesEvolution) || this.access.hasOTBView
    );
  }

  @dependentKeyCompat
  get selectedOTA(): Ota | null {
    return this.store.peekRecord<Ota>('ota', this.otaId);
  }

  get selectedTableSettingOptions(): TableSettingOption[] {
    const tableColumns = this.flatTableSettingOptions.filter(({ id }) => {
      return !this.hiddenColumnIdsWithFallback.includes(id);
    });

    const selectedTableComparisonLayoutOption = this.tableSettingOptions.find(
      ({ id }) => {
        return id === this.tableComparisonLayout;
      },
    );

    if (selectedTableComparisonLayoutOption) {
      return [...tableColumns, selectedTableComparisonLayoutOption];
    }

    return tableColumns;
  }

  get showOTBSelfServiceButton(): boolean {
    return (
      !this.myotbSs.isOtbSelfServiceUpsellBannerVisible &&
      this.access.hasFeature('otb.empty_placeholder')
    );
  }

  @computed('model.[]', 'isValidMonthToShowUpdateTime', 'timestamp')
  get showUpdateTime(): string | null {
    if (!this.isValidMonthToShowUpdateTime) {
      return null;
    }
    return this.timestamp;
  }

  get sortedDays(): Day[] {
    return this.monthOnlyModel
      .map(({ day }) => day)
      .sort((a, b) => Number(a.id) - Number(b.id));
  }

  /*
    The rate comparison display setting is persisted when changing subs,
    while table column visibility is persisted per hotel.
  */
  get tableComparisonLayout(): TableComparisonLayout {
    if (this.tableComparisonLayoutLocalState) {
      return this.tableComparisonLayoutLocalState;
    }

    const localStorageEntry = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.RATE_TABLE_COMPARISON_LAYOUT,
    );
    if (localStorageEntry) {
      // @ts-expect-error: Incorrect type
      return JSON.parse(localStorageEntry) as unknown;
    }

    return TABLE_COMPARISON_LAYOUT_DEFAULT;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get tableSettingOptions(): TableSettingOption[] {
    const tableColumns: TableSettingOption[] = [];

    if (this.access.hasFeature('otb.market_benchmark')) {
      tableColumns.push({
        id: RATE_COLUMNS.MARKET_OTB,
        label: this.intl.t('rates.tooltip.market_otb'),
      });
    }

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      tableColumns.push({
        id: RATE_COLUMNS.SHORT_TERM_RENTAL_OTB,
        label: this.intl.t('short_term_rental.otb'),
      });
    }

    if (this.access.hasFeature('subscription.demand')) {
      tableColumns.push({
        id: RATE_COLUMNS.MARKET_DEMAND,
        label: this.intl.t('demand.market_demand'),
      });
    }

    const activeCompsetOptions = (
      this.competitorsForCurrentHotelAndOTA ?? []
    ).map((competitor) => ({
      id: competitor.id!,
      label: competitor.name,
    }));

    const activeCompset = this.hotelAndMarketService.activeCompset;

    tableColumns.push({
      id: activeCompset?.id ?? '',
      groupName: activeCompset ? this.intl.t(activeCompset.translationKey) : '',
      options: activeCompsetOptions,
    });

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      tableColumns.push({
        id: RATE_COLUMNS.SHORT_TERM_RENTAL_RATE,
        label: this.intl.t('short_term_rental.rate'),
      });
    }

    const comparisonLayoutOption: TableSettingOption = {
      id: TABLE_COMPARISON_LAYOUT.SIDE_BY_SIDE,
      label: this.intl.t('rates.table.comparison_layout.label'),
      unsafe_subLabel: this.intl.t('rates.table.comparison_layout.sub_label'),
    };

    return [...tableColumns, comparisonLayoutOption];
  }

  @dependentKeyCompat
  get timestamp(): string | null {
    if (this.updateTimestamp !== null) {
      return this.updateTimestamp.fromNow();
    }
    return null;
  }

  @computed(
    'activeLiveUpdate',
    'compareType',
    'competitorsForCurrentHotelAndOTA.[]',
    'hotel.hotelinfo.id',
    'hotelAndMarketService.hotel.hotelinfo.id',
    'isPeriodsRequestLoading',
    'monthOnlyModel.@each.{fromDate,rates}',
  )
  get updateTimestamp(): Dayjs | null {
    let updateTimestamp: Dayjs | null = null;

    if (this.isPeriodsRequestLoading) {
      return updateTimestamp;
    }

    const ownHotelinfoId = this.hotelAndMarketService.hotel.hotelinfo.id!;
    const startOfDay = dayjs.utc().startOf('day');
    const oneHourAgoInUnix = dayjs.utc().subtract(1, 'hour').unix();

    // Array of arrays of unix extract_datetimes per period
    const extractDatetimesUnix: number[][] = [];

    this.monthOnlyModel?.forEach((period) => {
      const extractDatetimesUnixPerPeriod: number[] = [];

      // Only take into account periods from today
      if (
        period.fromDate.isSameOrAfter(startOfDay, 'day') &&
        period.rates[ownHotelinfoId]
      ) {
        const ownHotelRateExtractDatetime = this.getOldestExtractDatetime(
          period.rates[ownHotelinfoId][0],
        );

        if (ownHotelRateExtractDatetime > 0) {
          extractDatetimesUnixPerPeriod.push(ownHotelRateExtractDatetime);
        }

        // If the own hotel has been updated in the past hour, we stop here and only look at the extract_datetime of the own hotel
        if (ownHotelRateExtractDatetime < oneHourAgoInUnix) {
          // If not: we go over the competitors and add their extract_datetimes to the updateTimesUnix array

          this.competitorsForCurrentHotelAndOTA?.forEach((competitor) => {
            // If the competitor has no rates, we skip it
            if (period.rates[competitor.id!]) {
              const competitorRateExtractDatetime =
                this.getOldestExtractDatetime(period.rates[competitor.id!]![0]);

              if (competitorRateExtractDatetime > 0) {
                extractDatetimesUnixPerPeriod.push(
                  competitorRateExtractDatetime,
                );
              }
            }
          });
        }
      }

      if (extractDatetimesUnixPerPeriod.length) {
        extractDatetimesUnix.push(extractDatetimesUnixPerPeriod);
      }
    });

    if (extractDatetimesUnix.length) {
      updateTimestamp =
        getUpdateTimestampFromExtractDatetimes(extractDatetimesUnix);
    }

    return updateTimestamp;
  }

  // Determines the ratio of missing rates/rate changes in the hotel and compset
  calculateMissingRatio(
    hotelAndCompset: Hotelinfo[],
    total: number,
    isRateChangeObject: boolean,
  ): number {
    const { monthOnlyModel: periods } = this;

    if (periods.length === 0) {
      return 0;
    }

    const numMissing = periods.reduce((accumulator, period) => {
      hotelAndCompset.forEach((hotelinfo) => {
        const baseRate = period.rates[hotelinfo.id!];
        let rateObj: PeriodRate[] | undefined = baseRate;

        if (isRateChangeObject) {
          // Put the rate change in rateObj instead of the base rate
          rateObj = baseRate?.[0]?.comparison_rates;
        }

        if (rateObj && rateObj[0]?.message === 'general.missing') {
          accumulator++;
        }
      });

      return accumulator;
    }, 0);

    return numMissing / total;
  }

  // Made this a function since it uses the getLocalTime function, which needs to be executed every time
  defaultRateChanges(): string[] {
    const possibleSelections: string[] = [];

    if (
      this.hotelAndMarketService.hotel?.defaultLosRates &&
      this.hotelAndMarketService.hotel.defaultLosRates.indexOf(this.los) > -1 &&
      this.selectedOTA?.hasRatechange
    ) {
      const availableRateChangeDaySelections = [1, 3, 7];

      availableRateChangeDaySelections.forEach((rateChangeDaySelection) => {
        possibleSelections.push(
          this.hotelAndMarketService
            .getLocalTime()
            .startOf('day')
            .add(-1 * rateChangeDaySelection, 'day')
            .format('YYYY-MM-DD'),
        );
      });
    }

    return possibleSelections;
  }

  // Check a rates object and its comparison_rates for their extract_datetime and return the oldest one
  getOldestExtractDatetime(ratesObject: PeriodRate): number {
    let extractDatetime = dayjs.utc(ratesObject.extract_datetime).unix();

    const comparisonRates = ratesObject.comparison_rates;
    const isCompareActive = isComparingRates(
      this.compareType,
      this.compareValue,
    );

    if (
      isCompareActive &&
      comparisonRates &&
      !isDateCompare(this.compareType)
    ) {
      const comparisonRatesExtractDatetime = dayjs
        .utc(comparisonRates[0]?.extract_datetime)
        .unix();
      if (
        comparisonRatesExtractDatetime > 0 &&
        comparisonRatesExtractDatetime < extractDatetime
      ) {
        extractDatetime = comparisonRatesExtractDatetime;
      }
    }

    return extractDatetime;
  }

  @action
  closeCustomModal(): void {
    set(this, 'isShowingCustomModal', false);
  }

  @action
  closeScheduleModal(): void {
    this.scheduleReport = false;
  }

  @action
  doRefresh(): boolean {
    // Bubble to route
    return true;
  }

  @action
  handleExport(exportChoice: {
    disabled?: boolean;
    id: string;
    selection?: number;
  }): void {
    if (exportChoice.disabled) {
      return;
    }

    if (exportChoice.id === 'custom_report') {
      this.router.transitionTo('hotel.rates.export');
      return;
    }

    if (exportChoice.id === 'schedule_report') {
      if (!this.isScheduleDisabled?.disabled) {
        set(this, 'scheduleReport', true);
      }
      return;
    }

    let url = '';
    const exportPath = '/apigateway/v1/app/download/rates';

    const params: Record<
      string,
      string | number | string[] | null | undefined
    > = {
      compset_ids: this.compsetId,
      hotel_id: this.hotel?.id,
      los: this.los,
      mealtype: this.mealPlan,
      membershiptype: this.membershipType,
      ota: this.otaId,
      persons: this.maxPersons,
      platform: this.platform,
      rate_param: this.rateParam,
      roomtype: this.roomType, // roomTypeCorrection
    };

    if (this.platform > PLATFORMS.PLATFORM_DESKTOP) {
      params['allow_labels[]'] = ['no_label', 'mobile_only'];
    }

    // true for "Next XXX days", false for "Selected Month"
    if (exportChoice.selection) {
      const start = this.hotelAndMarketService.getLocalTime();
      params['from_date_range_start'] = start.format('YYYY-MM-DD');
      params['from_date_range_end'] = start
        .add(exportChoice.selection, 'day')
        .format('YYYY-MM-DD');

      mapCompareParamsToAPIParams(this, params);

      if (!isComparingRates(this.compareType, this.compareValue)) {
        // In case we compare with nothing
        this.defaultRateChanges().forEach((ratechange, index) => {
          params[`compare${index + 1}_extract_date`] = ratechange;
        });
      }

      url = formatUrl(exportPath, params, this.ajax.host);

      this.usageAnalytics.track('Data Exported', {
        days: exportChoice.selection,
        eventTrigger: 'filter-bar',
        exportType: exportChoice.id,
      });
    } else {
      params['from_date_range_start'] = this.monthMoment
        .startOf('month')
        .format('YYYY-MM-DD');
      params['from_date_range_end'] = this.monthMoment
        .endOf('month')
        .format('YYYY-MM-DD');

      mapCompareParamsToAPIParams(this, params);

      if (
        !isComparingRates(this.compareType, this.compareValue) &&
        !this.isPast
      ) {
        this.defaultRateChanges().forEach((ratechange, index) => {
          params[`compare${index + 1}_extract_date`] = ratechange;
        });
      }

      url = formatUrl(exportPath, params, this.ajax.host);

      this.usageAnalytics.track('Data Exported', {
        days: 30,
        eventTrigger: 'filter-bar',
        exportType: exportChoice.id,
      });
    }

    openUrl(url);
  }

  @action
  onOccupancyChartClicked(e: { point: { x: number } }): void {
    if (!this.ratesEvolutionOrBookingCurveEnabled) {
      return;
    }

    const day = dayjs.utc(e.point.x).format('YYYY-MM-DD');

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.rates.detail', {
      queryParams: {
        date: day,
        type: DETAIL_TABS.BOOKING_CURVE,
      },
    });
  }

  @action
  onPricingAssistantUpsellBannerDismiss(): void {
    this.localStorage.setItem(
      this.localStorageKeyPAUpsellBannerVisibility,
      JSON.stringify(false),
    );

    // Update banner visibility state to trigger reactivity i.e. UI update
    this.bannerVisibilityStates[this.localStorageKeyPAUpsellBannerVisibility] =
      false;
  }

  @action
  onPricingAssistantUpsellBannerPrimaryButtonClick(): void {
    this.showPAUpsellModal(true);
  }

  @action
  redirectToDayDetail(day: string): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.rates.detail', {
      queryParams: { date: day },
    });
  }

  @action
  selectionsChanged(selection: string, value: unknown): void {
    switch (selection) {
      case 'ota': {
        set(this, 'otaId', value);
        break;
      }

      case 'platform': {
        set(this, selection, value);
        break;
      }

      default: {
        set(this, selection, value);
      }
    }

    // Clear cached rates for other LOS (in tooltips) after changing filters
    this.rates.clearCachedLosRestrictionRates();

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      filterType: selection,
      filterValue: value,
    });
  }

  @action
  setBannerHeight(element: HTMLElement | null): void {
    this.bannerHeight = element?.offsetHeight ?? 0;
  }

  @action
  setCompsetId(compsetId: string): void {
    set(this, 'compsetId', compsetId);

    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      filterType: 'compset',
      filterValue: compsetId,
    });
  }

  @action
  showCalendarSettingsModal(isVisible: boolean): void {
    this.isShowingCalendarSettingsModal = isVisible;
  }

  @action
  showPAUpsellModal(isVisible: boolean): void {
    this.isShowingPAUpsellModal = isVisible;
  }

  @action
  startUpdate(): void {
    if (!this.liveupdateEnabled && !this.dayLiveupdateEnabled) {
      void this.alerting.showErrorAlert({
        text: this.intl.t('error.liveupdate.not_allowed_for_hotel'),
        title: this.intl.t('error.liveupdate.disabled'),
      });

      return;
    }

    if (
      !this.liveupdate.isPolling &&
      !this.isPast &&
      this.selectedOTA?.hasLiveShop
    ) {
      if (!this.liveupdate.isMonthLiveupdateLimitReached) {
        this.usageAnalytics.track('Live Shop Started', {
          eventTrigger: 'filter-bar',
          los: this.los,
          monthsOffsetFromToday: this.monthMoment.diff(
            this.hotelAndMarketService.getLocalTime().startOf('month'),
            'month',
          ),
          ota: this.otaId,
        });

        this.liveupdate.startLiveRateShop();
      }
    } else if (this.isPast) {
      void this.alerting.showErrorAlert({
        text: this.intl.t('error.liveupdate.to_date_from_date'),
        title: this.intl.t('error.liveupdate.historical_not_possible'),
      });
    }
  }

  @action
  switchTab(viewSwitcherButtonId: string): void {
    this.usageAnalytics.track('Data Visualisation Changed', {
      eventTrigger: 'filter-bar',
      from: this.view,
      to: viewSwitcherButtonId,
    });

    set(this, 'view', viewSwitcherButtonId);
    set(this, 'compareType', '');
    set(this, 'compareValue', '');
  }

  @action
  updatePageMode(targetPageMode: string): void {
    if (this.pageMode === targetPageMode) {
      return;
    }

    const subscriptionId = this.hotelAndMarketService.hotel.id;

    if (subscriptionId) {
      this.navigation.saveSubscription(
        'hotel.pricing-manager.index',
        subscriptionId,
      );
    }

    this.router.transitionTo('hotel.pricing-manager.index', {
      queryParams: {
        maxPersons: this.maxPersons,
        month: this.month,
        roomType: this.roomType,
      },
    });
  }

  @action
  updateSelectedMonth(selectedRange: { start: Dayjs } | null): void {
    if (!selectedRange) {
      return;
    }

    set(this, 'month', selectedRange.start.format('YYYY-MM'));
  }

  @action
  updateTableComparisonLayout(options: TableSettingOption[]): void {
    const selectedLayout = options.find(({ id }) => {
      return id === TABLE_COMPARISON_LAYOUT.SIDE_BY_SIDE;
    });

    this.tableComparisonLayoutLocalState =
      (selectedLayout?.id as TableComparisonLayout | undefined) ??
      TABLE_COMPARISON_LAYOUT_DEFAULT;

    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.RATE_TABLE_COMPARISON_LAYOUT,
      JSON.stringify(this.tableComparisonLayoutLocalState),
    );
  }

  @action
  updateTableSettings(columns: TableSettingOption[]): void {
    const difference = this.flatTableSettingOptions.filter((option) => {
      return !columns.includes(option);
    });

    this.hiddenColumnIds = difference.map(({ id }) => id);

    this.localStorage.setItem(
      this.localStorageKeyForHiddenColumns,
      JSON.stringify([...this.hiddenColumnIds]),
    );

    this.updateTableComparisonLayout(columns);

    this.usageAnalytics.track('Table Settings Changed', {
      comparisonDisplayType: this.tableComparisonLayoutLocalState,
      eventTrigger: 'filter-bar',
    });
  }
}

export default HotelRatesController;
